import { FC, SetStateAction, useEffect, useState } from 'react';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { TextField } from '@mui/material';

import jaLocale from 'date-fns/locale/ja';
import { DateTime } from 'luxon';

import {
  DateInputFormat,
  MonthInputFormat,
  YearInputFormat,
  MonthOutputFormat,
  DateOutputFormat,
  YearOutputFormat,
} from '@constants_iqloud';

import styles from './DatePickerWrapper.module.scss';

type Props = {
  state: any;
  setState: SetStateAction<any>;
  //ツールに表示するフォーマット
  inputType: 'year' | 'month' | 'day';
  //フォームで送信するフォーマット
  outputType: 'year' | 'month' | 'day';
  size?: string;
  onChangeFunc?: any;
  className?: string;
  dataTestId?: string;
  minDate?: Date;
  placeholder?: string;
};

const DatePickerWrapper: FC<Props> = ({
  state,
  setState,
  inputType,
  outputType,
  size,
  onChangeFunc,
  className,
  dataTestId,
  minDate,
  placeholder,
}) => {
  const [dateValidity, setDateValidity] = useState(true);
  const [showInputValue, setShowInputValue] = useState<any>(null);
  const validateInput = (value: string) => {
    inputType === 'day' && setDateValidity(DateTime.fromFormat(value, DateInputFormat).isValid);
    inputType === 'month' && setDateValidity(DateTime.fromFormat(value, MonthInputFormat).isValid);
    inputType === 'year' && setDateValidity(DateTime.fromFormat(value, YearInputFormat).isValid);
    value === '' && setDateValidity(true);
  };

  const inputFormat = () => {
    switch (inputType) {
      case 'year':
        return YearInputFormat;
      case 'month':
        return MonthInputFormat;
      case 'day':
        return DateInputFormat;
    }
  };
  const outputFormat = () => {
    switch (outputType) {
      case 'year':
        return YearInputFormat;
      case 'month':
        return MonthInputFormat;
      case 'day':
        return DateInputFormat;
    }
  };
  const toolbarFormat = () => {
    switch (inputType) {
      case 'year':
        return YearOutputFormat;
      case 'month':
        return MonthOutputFormat;
      case 'day':
        return DateOutputFormat;
    }
  };
  const calendarViews = (): DatePickerView[] => {
    switch (inputType) {
      case 'year':
        return ['year'];
      case 'month':
        return ['year', 'month'];
      case 'day':
        return ['year', 'month', 'day'];
    }
  };

  useEffect(() => {
    if (state) {
      setShowInputValue(state);
    } else {
      setShowInputValue(null);
    }
  }, [state]);

  const japaneseYearText = (inputValue: string) => {
    if (DateTime.fromFormat(inputValue, inputFormat()).isValid) {
      let date;
      if (inputType === 'year') {
        date = DateTime.fromFormat(inputValue + '-01' + '-01', DateInputFormat).toJSDate();
      } else if (inputType === 'month') {
        date = DateTime.fromFormat(inputValue + '-01', DateInputFormat).toJSDate();
      } else if (inputType === 'day') {
        date = DateTime.fromFormat(inputValue, DateInputFormat).toJSDate();
      }
      return new Intl.DateTimeFormat('ja-JP-u-ca-japanese', { era: 'long', year: 'numeric' }).format(date);
    } else {
      return '';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={jaLocale}>
      <DatePicker
        views={calendarViews()}
        value={showInputValue}
        inputFormat={inputFormat()}
        onChange={(newValue) => {
          if (newValue) {
            setState(DateTime.fromJSDate(newValue).toFormat(outputFormat()));
          } else {
            setState('');
          }
          setDateValidity(true);
          if (onChangeFunc) {
            onChangeFunc();
          }
        }}
        className={`${styles.date_picker}`}
        renderInput={(params) => (
          <TextField
            className={`${styles.input_date} ${!dateValidity && styles.input_date_invalid}
            ${size === 'width1' && 'util-formWidth1'}
            ${size === 'width2' && 'util-formWidth2'} ${className}`}
            {...params}
            inputProps={{
              ...params.inputProps,
              ...(placeholder ? { placeholder } : {}),
            }}
            value={showInputValue}
            onChange={(event) => validateInput(event.target.value)}
            helperText={japaneseYearText(params.inputProps?.value)}
            data-testid={dataTestId}
          />
        )}
        okText="決定"
        cancelText="キャンセル"
        clearable
        clearText="クリア"
        toolbarFormat={toolbarFormat()}
        toolbarTitle="" //'Select date'を非表示
        toolbarPlaceholder={''} //上部の"_"を非表示
        DialogProps={{ className: styles.date_picker_dialog }}
        disableCloseOnSelect={false}
        minDate={minDate}
      />
    </LocalizationProvider>
  );
};

export default DatePickerWrapper;
